/**
 * Error message mappings based on backend errType values.
 * This allows us to show custom user-friendly messages for specific error types
 * instead of the generic "We're having trouble processing your request" message.
 */

export interface ErrorMessageMapping {
  [errType: string]: string;
}

/**
 * Maps backend error types to user-friendly messages.
 * Add new mappings here when you want to customize the error message for specific error types.
 */
export const ERROR_MESSAGES: ErrorMessageMapping = {
  // Thread-related errors
  too_many_messages: "This thread has reached the maximum number of messages. Please start a new thread to continue.",
  insufficient_tokens: "You've reached your usage limit. Please upgrade your plan or try again later.",
  invalid_scope: "You don't have permission to access this resource. Please check your access permissions.",
  not_found_or_unauthorized: "The requested content could not be found or you don't have permission to access it.",

  // Data validation errors
  malformed_data: "The data you provided is invalid. Please check your input and try again.",
  invalid_params: "Some of the information provided is invalid. Please review and try again.",
  invalid_external_data: "The data format is not recognized. Please ensure you're using the correct format.",

  // Authentication and authorization errors
  token_or_grants_not_found: "Your session has expired. Please sign in again.",

  // System errors
  cloud_service_error: "We're experiencing technical difficulties. Please try again in a few moments.",
  llm_error: "Our AI service is temporarily unavailable. Please try again shortly.",
  stream_response:
    "We encountered a problem while delivering your response. This can happen due to a temporary connection issue or high server load. Please try your prompt again, it should work normally.",
  streamed_to_client:
    "Your response was interrupted during delivery. This typically occurs when there's a brief network hiccup or server timeout. Please try your prompt again to get your complete response.",

  // File and attachment errors
  attachment_not_found:
    "The requested file could not be found. It may have been removed or you may not have access to it.",

  // Generic fallback for unknown error types
  unknown: "We're having trouble processing your request. Please try again.",
  captured_internal_error:
    "The server encountered an unexpected condition that prevented it from fulfilling the request. Our team has been notified.",
};

/**
 * Maps backend error types to appropriate header messages for the error display.
 * These are used for the main error title/header, while ERROR_MESSAGES are used for the detailed message.
 */
export const ERROR_HEADER_MESSAGES: ErrorMessageMapping = {
  // Thread-related errors
  too_many_messages: "Thread limit reached",
  insufficient_tokens: "Usage limit reached",
  invalid_scope: "Access denied",
  not_found_or_unauthorized: "Content not found",

  // Data validation errors
  malformed_data: "Invalid data",
  invalid_params: "Invalid information",
  invalid_external_data: "Invalid format",

  // Authentication and authorization errors
  token_or_grants_not_found: "Session expired",

  // System errors
  cloud_service_error: "Service unavailable",
  llm_error: "AI service unavailable",
  stream_response: "Connection issue",
  streamed_to_client: "Connection issue",

  // File and attachment errors
  attachment_not_found: "File not found",

  // Generic fallback for unknown error types
  unknown: "Something went wrong",
  captured_internal_error: "Unexpected error",
};

/**
 * Default error message to show when no specific mapping exists for an error type.
 */
export const DEFAULT_ERROR_MESSAGE = "We're having trouble processing your request";

/**
 * Gets a user-friendly error message based on the backend errType.
 *
 * @param errType - The error type from the backend operation details
 * @param fallbackMessage - Optional custom fallback message
 * @returns User-friendly error message
 */
export function getErrorMessage(errType?: string, fallbackMessage: string = DEFAULT_ERROR_MESSAGE): string {
  if (!errType) {
    return fallbackMessage;
  }

  return ERROR_MESSAGES[errType] || fallbackMessage;
}

/**
 * Gets an appropriate header message based on the backend errType.
 *
 * @param errType - The error type from the backend operation details
 * @param fallbackMessage - Optional custom fallback message
 * @returns Header message for the error display
 */
export function getErrorHeaderMessage(errType?: string, fallbackMessage = "Something went wrong"): string {
  if (!errType) {
    return fallbackMessage;
  }

  return ERROR_HEADER_MESSAGES[errType] || fallbackMessage;
}

/**
 * Strips the prefix from an errType string (e.g., "threads.too_many_messages" -> "too_many_messages")
 */
function stripErrTypePrefix(errType: string): string {
  const dotIndex = errType.indexOf(".");
  return dotIndex > 0 ? errType.substring(dotIndex + 1) : errType;
}

/**
 * Extracts the errType from an APIError's details.operation.errType field or from the message.
 * Also handles cases where errType is embedded in the error message format like "prefix.errType: message".
 * Strips any prefix from the errType to match our ERROR_MESSAGES keys.
 *
 * @param error - The APIError object from the websocket service
 * @returns The errType string or undefined if not found
 */
export function extractErrType(error: unknown): string | undefined {
  try {
    // First try to get errType from details.operation.errType
    const errorObj = error as Record<string, unknown>;
    const details = errorObj?.details as Record<string, unknown> | undefined;
    const operation = details?.operation as Record<string, unknown> | undefined;
    const errTypeFromDetails = operation?.errType as string | undefined;

    if (errTypeFromDetails) {
      return stripErrTypePrefix(errTypeFromDetails);
    }

    // If not found, try to extract from the error message using pattern "prefix.errType: message"
    const message = errorObj?.message as string | undefined;
    if (message && typeof message === "string") {
      // Match patterns like "threads.too_many_messages: foo" or "service.error_type: description"
      const match = message.match(/^[a-zA-Z_]+\.([a-zA-Z_]+):/);
      if (match?.[1]) {
        return match[1];
      }
    }

    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Gets a user-friendly error message from an APIError object.
 * This is a convenience function that combines errType extraction and message lookup.
 *
 * @param error - The APIError object from the websocket service
 * @param fallbackMessage - Optional custom fallback message
 * @returns User-friendly error message
 */
export function getErrorMessageFromAPIError(error: unknown, fallbackMessage = DEFAULT_ERROR_MESSAGE): string {
  const errType = extractErrType(error);
  return getErrorMessage(errType, fallbackMessage);
}

/**
 * Gets an appropriate header message from an APIError object.
 * This is a convenience function that combines errType extraction and header message lookup.
 *
 * @param error - The APIError object from the websocket service
 * @param fallbackMessage - Optional custom fallback message
 * @returns Header message for the error display
 */
export function getErrorHeaderMessageFromAPIError(error: unknown, fallbackMessage = "Something went wrong"): string {
  const errType = extractErrType(error);
  return getErrorHeaderMessage(errType, fallbackMessage);
}
